
import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

const aiFormSchema = z.object({
  student_id: z.string().min(1, "Por favor, selecione um aluno"),
  activity_level: z.string().min(1, "Por favor, selecione o nível de atividade"),
  goals: z.string().min(1, "Por favor, selecione o objetivo"),
});

const manualFormSchema = z.object({
  student_id: z.string().min(1, "Por favor, selecione um aluno"),
  activity_level: z.string().min(1, "Por favor, selecione o nível de atividade"),
  goals: z.string().min(1, "Por favor, selecione o objetivo"),
  calories: z.string().min(1, "Por favor, insira as calorias"),
  protein: z.string().min(1, "Por favor, insira as proteínas"),
  carbs: z.string().min(1, "Por favor, insira os carboidratos"),
  fats: z.string().min(1, "Por favor, insira as gorduras"),
  food_suggestions: z.string().min(1, "Por favor, insira sugestões de alimentos"),
});

export const NutritionForm = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: recommendationId } = useParams();
  const isEditMode = !!recommendationId;
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("nutrition");
  const { language } = useLanguage(); // Import language from context
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recommendations, setRecommendations] = useState<{
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
    foodSuggestions: string;
    disclaimer?: string;
  } | null>(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [notes, setNotes] = useState("");
  const [mode, setMode] = useState<"ai" | "manual">("ai");

  const { data: students } = useQuery({
    queryKey: ["students"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("trainer_id", user?.id)
        .eq("status", "active");

      if (error) throw error;
      return data;
    },
  });

  const { data: existingRecommendation, isLoading: isLoadingRecommendation } = useQuery({
    queryKey: ["nutrition-recommendation", recommendationId],
    queryFn: async () => {
      if (!recommendationId) return null;

      const { data, error } = await supabase
        .from("nutrition_recommendations")
        .select("*")
        .eq("id", recommendationId)
        .eq("trainer_id", user?.id)
        .single();

      if (error) {
        toast.error("Erro ao carregar recomendação");
        console.error("Error loading recommendation:", error);
        return null;
      }
      
      return data;
    },
    enabled: isEditMode && !!recommendationId && !!user?.id
  });

  const aiForm = useForm<z.infer<typeof aiFormSchema>>({
    resolver: zodResolver(aiFormSchema),
    defaultValues: {
      student_id: "",
      activity_level: "",
      goals: "",
    },
  });

  const manualForm = useForm<z.infer<typeof manualFormSchema>>({
    resolver: zodResolver(manualFormSchema),
    defaultValues: {
      student_id: "",
      activity_level: "",
      goals: "",
      calories: "",
      protein: "",
      carbs: "",
      fats: "",
      food_suggestions: "",
    },
  });

  useEffect(() => {
    if (existingRecommendation && !isLoadingRecommendation) {
      // Set form values based on existing recommendation
      if (existingRecommendation.food_suggestions) {
        // This was likely a manual entry
        setMode("manual");
        manualForm.reset({
          student_id: existingRecommendation.student_id,
          activity_level: existingRecommendation.activity_level,
          goals: existingRecommendation.goals || "",
          calories: existingRecommendation.calories?.toString() || "",
          protein: existingRecommendation.protein?.toString() || "",
          carbs: existingRecommendation.carbs?.toString() || "",
          fats: existingRecommendation.fats?.toString() || "",
          food_suggestions: existingRecommendation.food_suggestions || "",
        });
        
        setRecommendations({
          calories: existingRecommendation.calories || 0,
          protein: existingRecommendation.protein || 0,
          carbs: existingRecommendation.carbs || 0,
          fats: existingRecommendation.fats || 0,
          foodSuggestions: existingRecommendation.food_suggestions || "",
        });
      } else {
        // AI generated entry
        aiForm.reset({
          student_id: existingRecommendation.student_id,
          activity_level: existingRecommendation.activity_level,
          goals: existingRecommendation.goals || "",
        });
        
        if (existingRecommendation.calories) {
          setRecommendations({
            calories: existingRecommendation.calories || 0,
            protein: existingRecommendation.protein || 0,
            carbs: existingRecommendation.carbs || 0,
            fats: existingRecommendation.fats || 0,
            foodSuggestions: "",
          });
        }
      }
      
      setNotes(existingRecommendation.notes || "");
      setShowSaveButton(true);
    }
  }, [existingRecommendation, isLoadingRecommendation, aiForm, manualForm]);

  const standardDisclaimer = "**Atenção:** As informações fornecidas aqui são apenas para fins educativos e informativos. Estas sugestões nutricionais são genéricas e não devem ser consideradas como aconselhamento nutricional personalizado. É fundamental consultar um nutricionista ou dietista qualificado para obter um plano alimentar individualizado e adaptado às tuas necessidades específicas, condições de saúde e objetivos.";

  const generateRecommendations = async (values: z.infer<typeof aiFormSchema>) => {
    try {
      setIsSubmitting(true);
      setShowSaveButton(false);

      const student = students?.find((s) => s.id === values.student_id);
      if (!student) {
        toast.error(t("errors.studentNotFound"));
        return;
      }

      const response = await supabase.functions.invoke('generate-nutrition', {
        body: {
          weight: student.weight,
          height: student.height,
          activityLevel: values.activity_level,
          goals: values.goals,
          medicalConditions: student.medical_conditions,
          language: language, // Pass the current language to the edge function
        },
      });

      if (response.error) throw response.error;
      
      setRecommendations({
        calories: response.data.calories,
        protein: response.data.protein,
        carbs: response.data.carbs,
        fats: response.data.fats,
        foodSuggestions: response.data.foodSuggestions,
        disclaimer: response.data.disclaimer
      });
      
      setShowSaveButton(true);
      toast.success(t("notifications.generated"));
    } catch (error: any) {
      console.error("Error generating nutrition recommendations:", error);
      toast.error(error.message || t("errors.generation"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSubmit = (values: z.infer<typeof manualFormSchema>) => {
    try {
      setRecommendations({
        calories: parseInt(values.calories),
        protein: parseInt(values.protein),
        carbs: parseInt(values.carbs),
        fats: parseInt(values.fats),
        foodSuggestions: values.food_suggestions,
        disclaimer: standardDisclaimer
      });
      
      setShowSaveButton(true);
      toast.success(t("notifications.generated"));
    } catch (error: any) {
      console.error("Error creating manual recommendations:", error);
      toast.error(error.message || "Erro ao criar recomendações manuais");
    }
  };

  const saveRecommendations = async () => {
    try {
      if (!recommendations) return;
      
      const student_id = mode === "ai" ? aiForm.getValues("student_id") : manualForm.getValues("student_id");
      const activity_level = mode === "ai" ? aiForm.getValues("activity_level") : manualForm.getValues("activity_level");
      const goals = mode === "ai" ? aiForm.getValues("goals") : manualForm.getValues("goals");
      
      if (!student_id) {
        toast.error("Por favor, selecione um aluno");
        return;
      }

      setIsSubmitting(true);
      
      const recommendationData = {
        student_id: student_id,
        trainer_id: user?.id,
        activity_level: activity_level,
        goals: goals,
        calories: recommendations.calories,
        protein: recommendations.protein,
        carbs: recommendations.carbs,
        fats: recommendations.fats,
        food_suggestions: recommendations.foodSuggestions,
        notes: notes,
      };
      
      let error;
      
      if (isEditMode && recommendationId) {
        // Update existing recommendation
        const { error: updateError } = await supabase
          .from("nutrition_recommendations")
          .update(recommendationData)
          .eq("id", recommendationId)
          .eq("trainer_id", user?.id);
          
        error = updateError;
      } else {
        // Insert new recommendation
        const { error: insertError } = await supabase
          .from("nutrition_recommendations")
          .insert([recommendationData]);
          
        error = insertError;
      }

      if (error) throw error;

      toast.success(isEditMode ? "Recomendação atualizada com sucesso!" : t("notifications.saved"));
      
      setTimeout(() => {
        navigate(getPath("/dashboard/nutrition"));
      }, 500);
    } catch (error: any) {
      console.error("Error saving nutrition recommendation:", error);
      toast.error(error.message || t("errors.saving"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight gradient-text">{t("form.newRecommendation")}</h2>
        <p className="text-muted-foreground dark:text-gray-300">{t("form.subtitle")}</p>
        <p className="text-sm text-muted-foreground dark:text-gray-400">{t("form.disclaimer")}</p>
        <Breadcrumb className="flex-wrap py-2">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">
                {t("breadcrumbs.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard/nutrition")} className="text-flame hover:text-flame/80">
                {t("title")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium dark:text-gray-200">{t("form.newRecommendation")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <Tabs defaultValue="ai" className="w-full" onValueChange={(value) => setMode(value as "ai" | "manual")}>
        <TabsList className="grid w-full grid-cols-2 mb-4 dark:bg-gray-700 dark:text-gray-200">
          <TabsTrigger value="ai" className="dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white">
            {t("form.aiGenerated")}
          </TabsTrigger>
          <TabsTrigger value="manual" className="dark:data-[state=active]:bg-gray-600 dark:data-[state=active]:text-white">
            {t("form.manualEntry")}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="ai" className="mt-0">
          <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-slate-100 dark:border-gray-700 shadow-sm">
            <Form {...aiForm}>
              <form onSubmit={aiForm.handleSubmit(generateRecommendations)} className="space-y-6">
                <FormField
                  control={aiForm.control}
                  name="student_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground dark:text-gray-200">{t("form.student")}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                            <SelectValue placeholder={t("form.selectStudent")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white dark:bg-gray-700">
                          {students?.map((student) => (
                            <SelectItem key={student.id} value={student.id} className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">
                              {student.first_name} {student.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={aiForm.control}
                  name="activity_level"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground dark:text-gray-200">{t("form.activityLevel")}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                            <SelectValue placeholder={t("form.selectActivityLevel")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white dark:bg-gray-700">
                          <SelectItem value="sedentario" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.sedentary")}</SelectItem>
                          <SelectItem value="leve" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.light")}</SelectItem>
                          <SelectItem value="moderado" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.moderate")}</SelectItem>
                          <SelectItem value="muito_ativo" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.very")}</SelectItem>
                          <SelectItem value="extremamente_ativo" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.extremely")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={aiForm.control}
                  name="goals"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground dark:text-gray-200">{t("form.goals")}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                            <SelectValue placeholder={t("form.selectGoals")} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="bg-white dark:bg-gray-700">
                          <SelectItem value="perda_peso" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.weightLoss")}</SelectItem>
                          <SelectItem value="ganho_massa" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.muscleGain")}</SelectItem>
                          <SelectItem value="manutencao" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.maintenance")}</SelectItem>
                          <SelectItem value="saude_geral" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.generalHealth")}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )}
                />

                <Button type="submit" disabled={isSubmitting} className="bg-flame text-white hover:bg-flame/90">
                  {isSubmitting ? t("form.generating") : t("form.generate")}
                </Button>
              </form>
            </Form>
          </Card>
        </TabsContent>
        
        <TabsContent value="manual" className="mt-0">
          <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-slate-100 dark:border-gray-700 shadow-sm">
            <Form {...manualForm}>
              <form onSubmit={manualForm.handleSubmit(handleManualSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={manualForm.control}
                    name="student_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("form.student")}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                              <SelectValue placeholder={t("form.selectStudent")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white dark:bg-gray-700">
                            {students?.map((student) => (
                              <SelectItem key={student.id} value={student.id} className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">
                                {student.first_name} {student.last_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="activity_level"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("form.activityLevel")}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                              <SelectValue placeholder={t("form.selectActivityLevel")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white dark:bg-gray-700">
                            <SelectItem value="sedentario" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.sedentary")}</SelectItem>
                            <SelectItem value="leve" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.light")}</SelectItem>
                            <SelectItem value="moderado" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.moderate")}</SelectItem>
                            <SelectItem value="muito_ativo" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.very")}</SelectItem>
                            <SelectItem value="extremamente_ativo" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("activityLevels.extremely")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="goals"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("form.goals")}</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600">
                              <SelectValue placeholder={t("form.selectGoals")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="bg-white dark:bg-gray-700">
                            <SelectItem value="perda_peso" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.weightLoss")}</SelectItem>
                            <SelectItem value="ganho_massa" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.muscleGain")}</SelectItem>
                            <SelectItem value="manutencao" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.maintenance")}</SelectItem>
                            <SelectItem value="saude_geral" className="dark:text-gray-200 dark:focus:bg-gray-600 dark:hover:bg-gray-600">{t("goals.generalHealth")}</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="calories"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("results.calories")} (kcal)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                        </FormControl>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="protein"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("results.protein")} (g)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                        </FormControl>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="carbs"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("results.carbs")} (g)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                        </FormControl>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={manualForm.control}
                    name="fats"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground dark:text-gray-200">{t("results.fats")} (g)</FormLabel>
                        <FormControl>
                          <Input type="number" {...field} className="bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" />
                        </FormControl>
                        <FormMessage className="dark:text-red-400" />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={manualForm.control}
                  name="food_suggestions"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground dark:text-gray-200">{t("results.foodSuggestions")}</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          rows={10}
                          className="min-h-[200px] bg-white dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600" 
                        />
                      </FormControl>
                      <FormMessage className="dark:text-red-400" />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="bg-flame text-white hover:bg-flame/90">
                  {t("form.generate")}
                </Button>
              </form>
            </Form>
          </Card>
        </TabsContent>
      </Tabs>

      {recommendations && (
        <Card className="p-6 space-y-4 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-slate-100 dark:border-gray-700 shadow-sm">
          <h3 className="text-xl font-semibold dark:text-white">{t("results.title")}</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground dark:text-gray-400">{t("results.calories")}</p>
              <p className="text-2xl font-bold dark:text-gray-200">{recommendations.calories}</p>
              <p className="text-xs text-muted-foreground dark:text-gray-400">{t("results.kcal")}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground dark:text-gray-400">{t("results.protein")}</p>
              <p className="text-2xl font-bold dark:text-gray-200">{recommendations.protein}</p>
              <p className="text-xs text-muted-foreground dark:text-gray-400">{t("results.grams")}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground dark:text-gray-400">{t("results.carbs")}</p>
              <p className="text-2xl font-bold dark:text-gray-200">{recommendations.carbs}</p>
              <p className="text-xs text-muted-foreground dark:text-gray-400">{t("results.grams")}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground dark:text-gray-400">{t("results.fats")}</p>
              <p className="text-2xl font-bold dark:text-gray-200">{recommendations.fats}</p>
              <p className="text-xs text-muted-foreground dark:text-gray-400">{t("results.grams")}</p>
            </div>
          </div>
          
          <div className="mt-4">
            <h4 className="font-medium mb-2 dark:text-white">{t("results.foodSuggestions")}</h4>
            <div className="whitespace-pre-wrap text-sm dark:text-gray-200 bg-slate-50 dark:bg-gray-700 p-4 rounded-lg">
              {recommendations.foodSuggestions}
            </div>
            
            <div className="mt-4 text-sm text-muted-foreground dark:text-gray-400 bg-slate-100 dark:bg-gray-600 p-4 rounded-lg">
              {(recommendations.disclaimer || standardDisclaimer).split(/\*\*(.*?)\*\*/g).map((part, index) => 
                index % 2 === 1 ? <strong key={index}>{part}</strong> : <span key={index}>{part}</span>
              )}
            </div>
          </div>

          <div className="mt-6">
            <h4 className="font-medium mb-2 dark:text-white">{t("form.notes")}</h4>
            <Textarea
              placeholder={t("form.notesPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600"
            />
          </div>

          {showSaveButton && (
            <Button 
              onClick={saveRecommendations} 
              className="mt-4 bg-flame text-white hover:bg-flame/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("form.generating") : t("form.save")}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
};
